/**
 * Per-bank statement import — the same smooth flow on desktop and mobile web:
 * the file input opens the native picker on phones (Files/Drive/camera), so
 * "share the PDF from your banking app, pick it here" just works. Upload →
 * AI-classified preview grouped into approvable sections → commit only what's
 * ticked. Learned merchants ("always match this") are saved for future imports.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { fmtR } from '../money.js';
import VoiceButton from './VoiceButton.jsx';
import AIAction from './AIAction.jsx';
import CadenceControls from './CadenceControls.jsx';
import { ensureAIConsent } from './AIGate.jsx';
import { useAuth } from '../App.jsx';

export default function ImportModal({ budget, bank, banks = [], categories = [],
                                      onClose, onApplied }) {
  const fileRef = useRef();
  const { me } = useAuth() || {};
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // approval state
  const [ticks, setTicks] = useState({});
  const [adds, setAdds] = useState({});
  const [recurs, setRecurs] = useState({});
  const [learns, setLearns] = useState({});
  const [combines, setCombines] = useState({});   // multi-month recurring votes
  const [formulas, setFormulas] = useState({});   // unit×count formula groups
  const [formulaRecurs, setFormulaRecurs] = useState({});
  const [groupSel, setGroupSel] = useState({});   // ⊕ hand-picked lines to group
  const [groupsOut, setGroupsOut] = useState([]); // built groups awaiting commit
  const [gForm, setGForm] = useState({ name: '', cat: '', formula: '',
                                       mode: 'new', itemId: '', recurring: false,
                                       spend: true, grow: false, growF: '' });
  const [aTargets, setATargets] = useState({});   // add-row target: new|item:ID|pend:J
  const [aCats, setACats] = useState({});         // add-row category override
  const [aFormulas, setAFormulas] = useState({}); // cluster-leader formula (adjustable)
  const [aExtend, setAExtend] = useState({});     // grow the target's calculation
  const [aExtendF, setAExtendF] = useState({});   // editable advancement (+42, *6…)
  const [fFormulas, setFFormulas] = useState({}); // formula-group formula override
  const [cRenames, setCRenames] = useState({});   // recurring-row custom names
  const [cAmts, setCAmts] = useState({});         // recurring-row amount/formula
  const [incomes, setIncomes] = useState({});     // detected income approvals
  const [iSalary, setISalary] = useState({});     // salary flag per income row
  const [iRenames, setIRenames] = useState({});
  const [iAmts, setIAmts] = useState({});         // income amount-or-formula
  const [showTransfers, setShowTransfers] = useState(false);
  const [txConfig, setTxConfig] = useState(null);  // {kind:'add'|'formula', i} in overlay
  const [txQ, setTxQ] = useState('');              // type-to-search an expense
  const [ovTab, setOvTab] = useState('details');   // overlay tab: details|where
  const [pausedRules, setPausedRules] = useState([]); // pause on Approve, not before
  const [fTargets, setFTargets] = useState({});    // formula-group destination
  const [fExtend, setFExtend] = useState({});      // group → grow target's formula
  const [fExtendF, setFExtendF] = useState({});    // its editable advancement
  const [iCats, setICats] = useState({});         // income category
  const [iDays, setIDays] = useState({});         // income expected day (1–28)
  const [iRecur, setIRecur] = useState({});       // income recurring override
  const [iFromMaster, setIFromMaster] = useState({}); // deduct-from-master choice
  const [renames, setRenames] = useState({});     // add-row custom names
  const [fRenames, setFRenames] = useState({});   // formula-row custom names
  const [fCats, setFCats] = useState({});         // formula-row custom groups
  const [itemBanks, setItemBanks] = useState({});  // per-item bank override
  const [useBalance, setUseBalance] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);   // collapse the AI summary (mockup: 'What Vault found')
  const [openMerch, setOpenMerch] = useState({});  // expanded merchant groups in New spending
  const [monthItems, setMonthItems] = useState(budget.items || []);  // fresh targets
  const [aSpend, setASpend] = useState({});   // count-as-spend (default on)
  const [priceOk, setPriceOk] = useState({});   // recurring price changes to apply
  const [pAmts, setPAmts] = useState({});       // advanced: a different new amount
  const [nsAct, setNsAct] = useState({});       // not-seen recurring: leave | paid | pause
  const [recAdv, setRecAdv] = useState({});     // which rows have Advanced open (r:i, p:i)
  const [cDays, setCDays] = useState({});       // advanced: day the recurring goes off
  const [cCats, setCCats] = useState({});       // advanced: recurring category
  const [fSpend, setFSpend] = useState({});   // group count-as-spend (default on)
  const [fOwnSpend, setFOwnSpend] = useState({}); // own-line: count observed spend
  const [fOwn, setFOwn] = useState({});           // own-line destination (default on)
  const [fSpentAmt, setFSpentAmt] = useState({}); // own-line editable spent amount
  const [fDropped, setFDropped] = useState({});   // {gi:{fp:true}} — members unticked
                                                  // OUT of a group, back to New spending
  // OWNER SPEC (Vault Import redesign): a line's LOCATION is editable ("📍 Link
  // a place"), and a new expense can be created FROM an import — the selected
  // transactions feed it going forward (a remembered rule), set up as a
  // one-month formula or a daily/weekly/monthly RECURRING cadence (Advanced).
  const [places, setPlaces] = useState([]);       // saved places for 📍 Link a place
  const [aLoc, setALoc] = useState({});           // add-row location (place name)
  const [fLoc, setFLoc] = useState({});           // formula-group location
  const [aCad, setACad] = useState({});           // add-row advanced cadence (CadenceControls shape)
  const [aOneF, setAOneF] = useState({});         // add-row one-month formula (e.g. 200*31)
  const [aFeed, setAFeed] = useState({});         // add-row: feed this expense from the merchant going forward
  const stmtBank = bank.bank ?? bank.id;

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return setError('Pick a statement file first.');
    // PDF or Excel only (#485) — the server refuses anything else too.
    if (!/\.(pdf|xlsx|xls)$/i.test(f.name || '')) {
      return setError('Upload your bank statement as a PDF or an Excel file (.pdf, .xlsx or .xls).');
    }
    // Apple 5.1.1(i) / POPIA: the statement is read by Gemini/Claude — ask first.
    // Before this the server answered "needs permission" with an EMPTY list,
    // which showed as "0 lines to review" and never reached the password step.
    if (!(await ensureAIConsent(me))) {
      return setError('Reading a statement uses AI (Google Gemini, with Anthropic Claude as backup). '
                      + 'Allow it to import, or add your items by hand.');
    }
    setBusy(true); setError('');
    const form = new FormData();
    form.append('file', f);
    form.append('bank', bank.bank ?? bank.id);
    if (password) form.append('password', password);
    try {
      const data = await api(`/budget/months/${budget.id}/reconcile_preview/`,
                             { method: 'POST', form });
      // Never show an empty review for a request that was not read at all.
      if (data?.ai_consent_required) {
        if (await ensureAIConsent(null)) { await upload(); return; }
        setError('Reading a statement uses AI. Allow it to import, or add your items by hand.');
        return;
      }
      if (data?.ai_off) {
        setError('AI is switched off right now, so statements can’t be read. You can still add items by hand.');
        return;
      }
      setNeedsPassword(false);
      setPreview(data);
      // Fresh month items so a just-created expense is searchable as a target.
      api(`/budget/items/?budget=${budget.id}`).then(setMonthItems).catch(() => {});
      // Saved places power the 📍 Link a place autocomplete — best-effort; an
      // empty list just means the location field accepts a free-typed name.
      api('/budget/places/').then((ps) => setPlaces(ps || [])).catch(() => {});
      const t = {}; (data.paid || []).forEach((p, i) => { t[i] = true; });
      setTicks(t);
      // New spending defaults UNCHECKED — loose one-off lines only enter the
      // budget when the user opts in; the grouped intelligence (formulas,
      // recurring) is where imports shine.
      setAdds({});
      // Detected income: salary pre-ticked (it's the point of the month),
      // salary flag carried from the AI guess — both still yours to flip.
      const inc = {}; const sal = {};
      (data.income_suggestions || []).forEach((s, i) => {
        inc[i] = !!s.is_salary; sal[i] = !!s.is_salary;
      });
      setIncomes(inc); setISalary(sal);
      const c = {}; (data.recurring_suggestions || []).forEach((r, i) => {
        c[i] = r.confidence === 'high';   // high-confidence pre-ticked, you still decide
      });
      setCombines(c);
      // A recurring charged at a new amount: the statement is the evidence, so
      // the update is pre-ticked. Pausing a recurring never is.
      const pc = {}; (data.price_changes || []).forEach((_, i) => { pc[i] = true; });
      setPriceOk(pc); setPAmts({}); setNsAct({}); setRecAdv({}); setCDays({}); setCCats({});
      // Formula groups: pre-tick only habits ACTIVE this cycle — a quiet one
      // (history says the habit may have stopped) stays offered, unticked,
      // so a dead pattern never budgets itself in. Ticked groups' member
      // lines auto-untick from "new spending" so nothing is added twice.
      const f = {}; const memberDescs = new Set();
      (data.formula_suggestions || []).forEach((g, i) => {
        f[i] = g.active_cycle !== false;
        if (f[i]) g.member_descs.forEach((d) => memberDescs.add(d));
      });
      setFormulas(f);
      if (memberDescs.size) {
        setAdds((prev) => {
          const next = { ...prev };
          (data.new_items || []).forEach((n, i) => {
            if (memberDescs.has(n.description)) next[i] = false;
          });
          return next;
        });
      }
    } catch (e) {
      console.error('Import preview failed:', e.status, e.data || e);
      // Only a LOCKED PDF asks for a password — "no transactions found" is a 422 too.
      if (e.data?.locked) setNeedsPassword(true);
      setError(e.data?.detail || e.message
               || 'The import failed — check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  // Lines consumed by a TICKED formula group (or a built ⊕ group) hide from
  // New spending — untick the group and they reappear, individually
  // assignable (to an expense as spend, or a formula advanced by units).
  const consumedDescs = new Set();
  (preview?.formula_suggestions || []).forEach((g, gi) => {
    if (formulas[gi]) (g.member_descs || []).forEach((d) => consumedDescs.add(d));
  });
  groupsOut.forEach((g) => (g.member_descs || []).forEach((d) => consumedDescs.add(d)));
  // A recurring being combined owns its statement line too — never also add
  // that same charge as new spending.
  (preview?.recurring_suggestions || []).forEach((r, ri) => {
    if (combines[ri]) (r.member_descs || []).forEach((d) => consumedDescs.add(d));
  });

  // Members UNTICKED out of a group are freed: they reappear in New spending
  // (individually assignable again) and everything the group commits — fps,
  // descs, observed spend, the unit×count formula — recomputes without them.
  const freedFps = new Set();
  Object.values(fDropped).forEach((m) =>
    Object.keys(m || {}).forEach((fp) => freedFps.add(fp)));
  const gLive = (g, gi) => {
    const members = g.members || [];
    if (!members.length) {
      return { live: [], spent: Number(g.spent || 0), formula: g.formula || '',
               fps: g.member_fps || [], descs: g.member_descs || [] };
    }
    const drops = fDropped[gi] || {};
    const live = members.filter((m) => !drops[m.fp]);
    const dropped = members.filter((m) => drops[m.fp]
      && (!preview?.cycle_start || !m.date || m.date >= preview.cycle_start));
    const spent = Math.max(0, Number(g.spent || 0)
      - dropped.reduce((s, m) => s + Number(m.amount || 0), 0));
    let formula = g.formula || '';
    const fm = String(formula).match(/^([0-9.]+)\*([0-9]+)$/);
    if (fm && dropped.length) {
      formula = `${fm[1]}*${Math.max(0, Number(fm[2]) - dropped.length)}`;
    }
    return { live, spent: Number(spent.toFixed(2)), formula,
             fps: live.map((m) => m.fp).filter(Boolean),
             descs: [...new Set(live.map((m) => m.description))] };
  };

  const commit = async () => {
    setBusy(true); setError('');
    // DYNAMIC add-rows: each checked line either stands alone (add), applies
    // to an EXISTING month expense (spend added to it, rule remembered), or
    // attaches to a NEW expense another row in this same list is creating —
    // those clusters commit as one grouped expense together.
    const addRows = (preview.new_items || []).map((n, i) => ({ n, i }))
      .filter(({ n, i }) => adds[i] && (!consumedDescs.has(n.description)
                                        || freedFps.has(n.fp)));
    const nameOf = ({ n, i }) =>
      (renames[i] || '').trim() || n.suggested_title || n.description;
    const clusters = {}; const routedGroups = []; const fpendExtra = {};
    for (const row of addRows) {
      const t = aTargets[row.i] || 'new';
      if (t.startsWith('item:')) {
        // The typed name RIDES ALONG: the backend notes it on the master
        // expense as a dated sub-line, so the application stays readable.
        routedGroups.push({ mode: 'existing', item_id: Number(t.slice(5)),
                            title: nameOf(row), category: '', formula: '',
                            total: row.n.amount, bank: itemBanks[row.i] ?? stmtBank,
                            recurring: false, remember: true,
                            extend: !!aExtend[row.i], spend: aSpend[row.i] ?? true,
                            extend_formula: (aExtendF[row.i] || '')
                              .replace(/[x×]/gi, '*').trim(),
                            member_descs: [row.n.description] });
      } else if (t.startsWith('fpend:')) {
        const gi = Number(t.slice(6));
        (fpendExtra[gi] = fpendExtra[gi] || []).push(row);
      } else if (t.startsWith('pend:')) {
        const j = Number(t.slice(5));
        (clusters[j] = clusters[j] || []).push(row);
      } else {
        clusters[row.i] = [row, ...(clusters[row.i] || [])];
      }
    }
    // Groups pointed at ANOTHER pending group fold into that group's line:
    // their money as spend and/or formula growth, their members' rules + fps.
    (preview.formula_suggestions || []).forEach((g, gi) => {
      const t2 = fTargets[gi] || '';
      if (formulas[gi] && t2.startsWith('fpend:')) {
        const j = Number(t2.slice(6));
        const lv = gLive(g, gi);
        (fpendExtra[j] = fpendExtra[j] || []).push({
          i: `g${gi}`,
          spendFlag: fSpend[gi] ?? true, growFlag: !!fExtend[gi],
          growF: fExtendF[gi] || '',
          n: { amount: lv.spent, descs: lv.descs,
               fps: lv.fps, description: g.title },
        });
      }
    });
    const plainAdds = [];
    for (const [j, rows] of Object.entries(clusters)) {
      const leader = rows.find((r) => r.i === Number(j)) || rows[0];
      if (rows.length === 1) {
        plainAdds.push(leader);
      } else {
        // Repeats become a CALCULATION when useful: the leader's formula box
        // (pre-hinted avg×count, fully adjustable) wins; else the plain sum.
        const total = rows.reduce((s, r) => s + Number(r.n.amount || 0), 0);
        const f = (aFormulas[leader.i] || '').replace(/[x×]/gi, '*').trim();
        // A multi-line new expense carries the leader's Advanced cadence and
        // 📍 location too, and its member lines are the transactions feeding it.
        const lcad = aCad[leader.i] || null;
        const lrecurring = !!recurs[leader.i]
          || (lcad && lcad.cadence && lcad.cadence !== 'none');
        routedGroups.push({
          mode: 'new', title: nameOf(leader),
          category: (aCats[leader.i] || '').trim() || leader.n.category || '',
          formula: /[+*/-]/.test(f.slice(1)) ? f : '',
          total: total.toFixed(2),
          bank: itemBanks[leader.i] ?? stmtBank,
          recurring: lrecurring, remember: true,
          cadence: lrecurring ? lcad : null,
          location: (aLoc[leader.i] || '').trim(),
          member_descs: rows.map((r) => r.n.description),
        });
      }
    }
    const approvals = {
      tick: [...(preview.paid || []).filter((_, i) => ticks[i]).map((p) => p.item.id),
             // a recurring not on this statement that the owner says is paid anyway
             ...(preview.not_seen || []).filter((_, i) => nsAct[i] === 'paid').map((n) => n.item_id)],
      receive: (preview.income || []).map((i) => i.item.id),
      add: plainAdds.map(({ n, i }) => {
        // A one-month formula (e.g. 200*31) sets the amount; an Advanced
        // cadence rides along so the recurring seeds each new month by its
        // cadence, not a flat copy. `location` links a saved place (or is
        // kept as a note). `member_descs` + `feed` teach a rule so these
        // transactions feed the expense on every future import.
        const cad = aCad[i] || null;
        const recurring = !!recurs[i] || (cad && cad.cadence && cad.cadence !== 'none');
        return {
          title: nameOf({ n, i }),
          amount: n.amount,
          category: (aCats[i] || '').trim() || n.category || '',
          bank: itemBanks[i] ?? stmtBank, recurring,
          formula: (aOneF[i] || '').replace(/[x×]/gi, '*').trim(),
          cadence: recurring ? cad : null,
          location: (aLoc[i] || '').trim(),
          member_descs: (aFeed[i] ?? recurring) ? [n.description] : [],
          remember: !!(aFeed[i] ?? recurring),
        };
      }),
      learn: (preview.paid || []).filter((_, i) => learns[i]).map((p) => ({
        match_text: p.description, item_id: p.item.id,
      })),
      // Groups pointed at an EXISTING expense apply there (spend or growth)
      // instead of becoming their own line.
      add_formula: (preview.formula_suggestions || []).map((g, i) => ({ g, i }))
        .filter(({ i }) => formulas[i] && (fOwn[i] ?? true)
                && !(fTargets[i] || '').startsWith('fpend:'))
        .map(({ g, i }) => {
          // Transactions pointed at this pending expense fold in: their spend
          // adds to spent (if flagged), their amounts grow the formula (if
          // flagged), their merchants learn rules, their fps mark applied.
          // Everything starts from the LIVE view — unticked members are out.
          const lv = gLive(g, i);
          const extra = fpendExtra[i] || [];
          let formula = (fFormulas[i] || '').replace(/[x×]/gi, '*').trim() || lv.formula;
          let spent = fSpentAmt[i] !== undefined
            ? Number(fSpentAmt[i] || 0)
            : ((fOwnSpend[i] ?? true) ? Number(lv.spent || 0) : 0);
          const descs = [...lv.descs];
          const fps = [...lv.fps];
          for (const row of extra) {
            const doSpend = row.spendFlag !== undefined ? row.spendFlag : (aSpend[row.i] ?? true);
            const doGrow = row.growFlag !== undefined ? row.growFlag : !!aExtend[row.i];
            if (doSpend) spent += Number(row.n.amount || 0);
            if (doGrow) {
              const ext = (row.growF !== undefined ? row.growF
                           : (aExtendF[row.i] || '')).replace(/[x×]/gi, '*').trim();
              formula += ext && '+-*/'.includes(ext[0]) ? ext : `+${row.n.amount}`;
            }
            if (Array.isArray(row.n.descs)) descs.push(...row.n.descs);
            else descs.push(row.n.description);
            if (Array.isArray(row.n.fps)) fps.push(...row.n.fps);
            else if (row.n.fp) fps.push(row.n.fp);
          }
          return {
            title: (fRenames[i] || '').trim() || g.title,
            formula,
            spent: spent > 0 ? spent.toFixed(2) : '',
            category: (fCats[i] || '').trim() || g.category,
            match_text: g.match_text, member_descs: descs, member_fps: fps,
            location: (fLoc[i] || '').trim(),
            bank: stmtBank, recurring: !!formulaRecurs[g.match_text] };
        }),
      pause_rules: pausedRules,
      group: [...groupsOut, ...routedGroups,
        ...(preview.formula_suggestions || []).map((g, i) => ({ g, i }))
          .filter(({ i }) => formulas[i] && (fTargets[i] || '').startsWith('item:'))
          .map(({ g, i }) => {
            const lv = gLive(g, i);
            return {
              mode: 'existing', item_id: Number(fTargets[i].slice(5)),
              title: (fRenames[i] || '').trim() || g.title,
              category: '', formula: '',
              total: lv.spent, bank: stmtBank, recurring: false, remember: true,
              extend: !!fExtend[i], spend: fSpend[i] ?? true,
              extend_formula: (fExtendF[i] || '').replace(/[x×]/gi, '*').trim(),
              member_descs: lv.descs, member_fps: lv.fps,
            };
          })],
      add_income: (preview.income_suggestions || []).map((s, i) => ({ s, i }))
        .filter(({ i }) => incomes[i])
        .map(({ s, i }) => {
          const v = (iAmts[i] || '').replace(/[x×]/gi, '*').trim();
          return {
            title: (iRenames[i] || '').trim() || s.title,
            amount: v && !/[+*/-]/.test(v.slice(1)) ? v : s.amount,
            formula: v && /[+*/-]/.test(v.slice(1)) ? v : '',
            category: (iCats[i] || '').trim(),
            expected_day: Number(iDays[i]) || (s.last_seen
              ? Number(s.last_seen.slice(8, 10)) : null),
            bank: stmtBank, is_salary: !!iSalary[i],
            recurring: iRecur[i] ?? !!s.recurring,
            from_master: iFromMaster[i] ?? true,
          };
        }),
      make_recurring: (preview.recurring_suggestions || []).map((r, i) => ({ r, i }))
        .filter(({ i }) => combines[i])
        .map(({ r, i }) => {
          const v = (cAmts[i] || '').replace(/[x×]/gi, '*').trim();
          return { title: (cRenames[i] || '').trim() || r.title,
                   amount: v && !/[+*/-]/.test(v.slice(1)) ? v : r.amount,
                   formula: v && /[+*/-]/.test(v.slice(1)) ? v : '',
                   category: (cCats[i] ?? r.category ?? '').trim(),
                   match_text: r.match_text, bank: bank.bank ?? bank.id,
                   // the day it usually goes off, and — because only patterns
                   // with a charge this cycle are offered — this month is paid.
                   expected_day: Number(cDays[i] ?? r.expected_day) || null, paid_now: true };
        }),
      update_recurring: (preview.price_changes || []).map((p, i) => ({ p, i }))
        .filter(({ i }) => priceOk[i])
        .map(({ p, i }) => ({ template_id: p.template_id,
                              amount: String(pAmts[i] ?? p.new_amount).replace(',', '.').trim() })),
      pause_recurring: (preview.not_seen || []).filter((_, i) => nsAct[i] === 'pause')
        .map((n) => n.template_id),
      bank_actual: useBalance && preview.suggest_actual_now
        ? [{ bank: bank.bank ?? bank.id, amount: preview.suggest_actual_now }] : [],
      // ONLY transactions actually USED get stored as applied — approving
      // nothing stores nothing, so everything stays suggestible next time.
      // Each fp travels WITH its destination title, so a re-import shows
      // every settled line struck through with where it went.
      applied_fps: (() => {
        const monthTitle = (id) =>
          (monthItems.find((x) => x.id === Number(id)) || {}).title || '';
        const destTitle = (i, fallback) => {
          const t = aTargets[i] || 'new';
          if (t.startsWith('item:')) return monthTitle(t.slice(5)) || fallback;
          if (t.startsWith('pend:')) {
            const j = Number(t.slice(5));
            const leader = preview.new_items?.[j];
            return leader ? nameOf({ n: leader, i: j }) : fallback;
          }
          if (t.startsWith('fpend:')) {
            const j = Number(t.slice(6));
            return (fRenames[j] || '').trim()
              || preview.formula_suggestions?.[j]?.title || fallback;
          }
          return fallback;
        };
        return [
          ...addRows.filter(({ i }) => adds[i]).map(({ n, i }) =>
            ({ fp: n.fp, to: destTitle(i, nameOf({ n, i })) })),
          ...(preview.formula_suggestions || []).flatMap((g, i) => {
            if (!formulas[i]) return [];
            const to = (fTargets[i] || '').startsWith('item:')
              ? monthTitle(fTargets[i].slice(5))
              : ((fRenames[i] || '').trim() || g.title);
            return gLive(g, i).fps.map((fp) => ({ fp, to }));
          }),
          ...groupsOut.flatMap((g) => (g.member_fps || []).map((fp) => ({
            fp, to: g.mode === 'existing' ? monthTitle(g.item_id) : g.title,
          }))),
        ].filter((e) => e.fp);
      })(),
      fees_total: preview.fees?.total, fees_bank: bank.bank ?? bank.id,
    };
    try {
      await api(`/budget/months/${budget.id}/reconcile_commit/`,
                { method: 'POST', body: approvals });
      onApplied();
    } catch (e) {
      setError(e.data?.detail || e.message);
      setBusy(false);
    }
  };

  // NO backdrop-close: a drag that ends outside must never discard a
  // half-reviewed import — only the Close button (or Apply) leaves.
  // The exact New-spending row, reused flat (singleton merchant) or inside an
  // expanded merchant group. Per-line controls (tick, configure, group) unchanged.
  const renderNewRow = (i) => {
    const n = preview.new_items[i];
    return (
      <div key={i} className="flex items-center gap-2 py-2 cursor-pointer
                                          hover:bg-white/[0.03] rounded-lg px-1 transition"
                       onClick={() => { setTxQ(''); setTxConfig({ kind: 'add', i }); }}>
                    <input type="checkbox" checked={!!adds[i]}
                           onClick={(e) => e.stopPropagation()}
                           onChange={() => setAdds((a) => ({ ...a, [i]: !a[i] }))} />
                    <span className="flex-1 min-w-[140px]">
                      <span className="text-sm">{(renames[i] || '').trim()
                        || n.suggested_title || n.description}</span>
                      {n.learned_new && (
                        <span className="text-xs block text-vblue">
                          remembered rule — usually “{n.suggested_title}”
                          {n.rule_id && (
                            <button className={`ml-2 underline ${
                                      pausedRules.includes(n.rule_id)
                                        ? 'text-red-400' : 'text-slate-500 hover:text-red-400'}`}
                                    title="Pausing applies when you Approve — future imports interpret the merchant fresh (resume in Manage → Rules)"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation();
                                      setPausedRules((r) => r.includes(n.rule_id)
                                        ? r.filter((x) => x !== n.rule_id)
                                        : [...r, n.rule_id]); }}>
                              {pausedRules.includes(n.rule_id)
                                ? 'pauses on Approve · undo' : 'pause rule'}
                            </button>
                          )}
                        </span>
                      )}
                      <span className="text-xs block">
                        <span className="text-vpurple">{(aCats[i] || '').trim() || n.category}</span>
                        {(aTargets[i] || 'new') !== 'new' && (
                          <span className="text-vblue"> · {aTargets[i].startsWith('pend:')
                            ? '↪ joins a new expense' : aTargets[i].startsWith('fpend:')
                            ? '↪ joins a new formula expense' : '→ applies to an existing expense'}
                            {aExtend[i] ? ' (grows formula)' : ''}</span>
                        )}
                        {recurs[i] && <span className="text-vpurple"> · every month</span>}
                      </span>
                    </span>
                    <span className="font-medium">{fmtR(n.amount)}</span>
                    <button className={`text-sm px-1.5 rounded border ${
                              groupSel[i] ? 'border-vblue text-vblue' : 'border-edge text-slate-600'}`}
                            title="Pick several lines, then bind them to ONE expense below"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation();
                              setGroupSel((v) => ({ ...v, [i]: !v[i] })); }}>
                      ⊕
                    </button>
                  </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-end md:place-items-center z-50">
      <div className="bg-panel border border-edge rounded-t-2xl md:rounded-2xl w-full md:w-[94vw] md:max-w-6xl
                      max-h-[92vh] overflow-y-auto p-6"
           data-guide-context="import"
           onClick={(e) => e.stopPropagation()}>
        <h2 className="font-bold text-lg">Import a {bank.bank_name ?? bank.name} statement</h2>
        {/* Type a category anywhere → choose an existing one or keep typing to
            create it new (then it's available to every other row too). */}
        <datalist id="vault-cats">
          {categories.map((c) => <option key={c.id} value={c.name} />)}
        </datalist>
        {/* Saved places for the 📍 Link a place field — type to match one, or
            keep typing a name; an unmatched name is kept as a note on the line. */}
        <datalist id="vault-places">
          {places.map((p) => <option key={p.id} value={p.name} />)}
        </datalist>
        {/* The modal must NEVER fail silently — whatever went wrong sits here,
            first thing the eye lands on. */}
        {error && (
          <p className="mt-3 text-sm text-red-300 border border-red-400/50 bg-red-400/10
                        rounded-xl px-4 py-2.5">
            {error}
          </p>
        )}

        {!preview && (
          <div className="mt-4 grid gap-3">
            <p className="text-slate-400 text-sm">
              PDF or Excel (.xlsx or .xls) — on your phone, share/save the statement from your banking
              app, then pick it here. Re-importing the same statement is always safe.
            </p>
            <input ref={fileRef} type="file" disabled={busy} accept=".pdf,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                   className="input file:mr-3 file:btn-primary file:border-0" />
            {needsPassword && (
              <div className="grid gap-1.5 rounded-xl border border-vpurple/50 p-3">
                <div className="text-sm font-medium">🔒 This statement is locked</div>
                <div className="text-xs text-slate-400">
                  Enter the PDF password to unlock it — for TymeBank, GoTyme and most banks it is your ID number.
                  It is used once to open the file and is not stored.
                </div>
                <input className="input" type="password" autoFocus autoComplete="off"
                       placeholder="PDF password (often your ID number)"
                       value={password} onChange={(e) => setPassword(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter' && password) upload(); }} />
              </div>
            )}
            {busy ? (
              <ImportProgress bankName={bank.bank_name ?? bank.name} />
            ) : (
              <button className="btn-primary" onClick={upload} disabled={needsPassword && !password}>
                {needsPassword ? 'Unlock & review' : 'Upload & review'}
              </button>
            )}
          </div>
        )}

        {preview && (() => {
          const secs = [];
          const addSec = (key, label, total, approved) => { if (total > 0) secs.push({ key, label, total, approved }); };
          addSec('paid', 'Looks paid', (preview.paid || []).length, (preview.paid || []).filter((_, i) => ticks[i]).length);
          addSec('income', 'Money in', (preview.income_suggestions || []).length, (preview.income_suggestions || []).filter((_, i) => incomes[i]).length);
          addSec('formula', 'Formula groups', (preview.formula_suggestions || []).length, (preview.formula_suggestions || []).filter((_, i) => formulas[i]).length);
          addSec('recurring', 'Recurring', (preview.recurring_suggestions || []).length, (preview.recurring_suggestions || []).filter((_, i) => recurs[i] || combines[i]).length);
          addSec('prices', 'Price changes', (preview.price_changes || []).length, (preview.price_changes || []).filter((_, i) => priceOk[i]).length);
          addSec('new', 'New spending', (preview.new_items || []).length, (preview.new_items || []).filter((_, i) => adds[i]).length);
          addSec('already', 'Already applied', (preview.already_applied || []).length, (preview.already_applied || []).length);
          const approvable = secs.filter((sx) => sx.key !== 'already');
          const totalRows = approvable.reduce((acc, sx) => acc + sx.total, 0);
          const approvedRows = approvable.reduce((acc, sx) => acc + sx.approved, 0);
          const pct = totalRows ? Math.round((100 * approvedRows) / totalRows) : 0;
          const jumpTo = (key) => { const el = document.getElementById('imp-' + key); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
          return (
          <div className="mt-4 flex gap-5 items-start">
            <aside className="hidden md:flex flex-col gap-3 w-52 shrink-0 sticky top-0 self-start">
              <div>
                <div className="font-semibold text-sm">{bank.bank_name ?? bank.name}</div>
                <div className="text-[11px] text-slate-500">{totalRows} line{totalRows === 1 ? '' : 's'} to review</div>
              </div>
              <div>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wide text-slate-500">Approved</span>
                  <span className="text-xs font-semibold text-vblue">{approvedRows} of {totalRows}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-vblue transition-all" style={{ width: pct + '%' }} />
                </div>
              </div>
              <div className="flex flex-col gap-0.5">
                {secs.map((sx) => (
                  <button key={sx.key} type="button" onClick={() => jumpTo(sx.key)}
                          className="flex items-center gap-2 text-left px-2 py-1.5 rounded-lg hover:bg-white/5 transition text-sm">
                    <span className="flex-1 min-w-0 truncate text-slate-300">{sx.label}</span>
                    <span className="text-[11px] text-slate-500 shrink-0">{sx.key === 'already' ? sx.total : sx.approved + '/' + sx.total}</span>
                  </button>
                ))}
              </div>
            </aside>
            <div className="flex-1 min-w-0 grid gap-4">
            {preview.summary && (
              <div className="bg-ink border border-edge rounded-xl px-4 py-3 text-sm">
                <button type="button" className="flex items-center gap-2 text-left w-full"
                        onClick={() => setAiOpen((v) => !v)}>
                  <span aria-hidden>✨</span>
                  <span className="font-semibold text-vpurple flex-1">What Vault found</span>
                  <span className="text-slate-500 text-xs">{aiOpen ? 'Hide ▾' : 'Show ▸'}</span>
                </button>
                {aiOpen && <p className="text-slate-300 leading-relaxed mt-2">{preview.summary}</p>}
              </div>
            )}

            {(preview.paid || []).length > 0 && (
              <Section id="imp-paid" title="Looks paid — tick them off">
                {preview.paid.map((p, i) => (
                  <label key={i} className="flex items-center gap-3 py-2">
                    <input type="checkbox" checked={!!ticks[i]}
                           onChange={() => setTicks((t) => ({ ...t, [i]: !t[i] }))} />
                    <span className="flex-1">{p.item.title}
                      <span className="text-slate-500 text-xs block">
                        {p.description}
                        {p.learned && p.rule_id && (
                          <button className={`ml-2 underline ${
                                    pausedRules.includes(p.rule_id)
                                      ? 'text-red-400' : 'text-vblue hover:text-red-400'}`}
                                  title="Routed by a remembered rule — pausing applies when you Approve; resume any time in Manage → Rules"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation();
                                    setPausedRules((r) => r.includes(p.rule_id)
                                      ? r.filter((x) => x !== p.rule_id)
                                      : [...r, p.rule_id]); }}>
                            {pausedRules.includes(p.rule_id)
                              ? 'rule pauses on Approve · undo' : 'via rule · pause'}
                          </button>
                        )}
                      </span>
                    </span>
                    <span className="font-medium">{fmtR(p.amount)}</span>
                    <button className={`text-xs px-2 py-1 rounded border ${
                              learns[i] ? 'border-vpurple text-vpurple' : 'border-edge text-slate-500'}`}
                            onClick={(e) => { e.preventDefault(); setLearns((l) => ({ ...l, [i]: !l[i] })); }}>
                      {learns[i] ? 'Will remember' : 'Always match'}
                    </button>
                  </label>
                ))}
              </Section>
            )}

            {(preview.income_suggestions || []).length > 0 && (
              <Section id="imp-income" title="Money in — income detected (salary spotted for you)">
                {preview.income_suggestions.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 cursor-pointer
                                          hover:bg-white/[0.03] rounded-lg px-1 transition"
                       onClick={() => { setTxQ(''); setTxConfig({ kind: 'income', i }); }}>
                    <input type="checkbox" checked={!!incomes[i]}
                           onClick={(e) => e.stopPropagation()}
                           onChange={() => setIncomes((v) => ({ ...v, [i]: !v[i] }))} />
                    <span className="flex-1 min-w-[140px]">
                      <span className="font-medium text-sm">{(iRenames[i] ?? s.title) || s.title}</span>
                      {iSalary[i] && <span className="ml-1.5 text-[10px] uppercase tracking-wide
                                            text-vpurple border border-vpurple rounded px-1">salary ✓</span>}
                      <span className="text-slate-500 text-xs block">
                        {s.months_seen.length > 1
                          ? `Lands every month · seen ${s.count}× across ${s.months_seen.length} months`
                          : 'Seen this cycle'}
                        {s.last_seen && ` · last ${s.last_seen}`}
                        {(iDays[i] || s.last_seen) && ` · day ${iDays[i] || Number(s.last_seen.slice(8, 10))}`}
                        {s.recurring && ' · will recur monthly'}
                      </span>
                    </span>
                    <span className="font-medium text-emerald-400">{fmtR(iAmts[i] ?? s.amount)}</span>
                  </div>
                ))}
              </Section>
            )}

            {(preview.formula_suggestions || []).length > 0 && (
              <Section id="imp-formula" title="Same thing many times — one line with a formula?">
                {preview.formula_suggestions.map((g, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 cursor-pointer
                                          hover:bg-white/[0.03] rounded-lg px-1 transition"
                       onClick={() => { setTxQ(''); setOvTab('details'); setTxConfig({ kind: 'formula', i }); }}>
                    <input type="checkbox" checked={!!formulas[i]}
                           onClick={(e) => e.stopPropagation()}
                           onChange={() => setFormulas((f) => ({ ...f, [i]: !f[i] }))} />
                    <span className="flex-1 min-w-[140px]">
                      <span className="font-medium text-sm">{(fRenames[i] ?? g.title) || g.title}</span>
                      <span className="text-vpurple text-xs"> · {(fCats[i] ?? g.category) || 'Other'}</span>
                      {formulaRecurs[g.match_text] && (
                        <span className="ml-1.5 text-[10px] text-vpurple">every month</span>)}
                      {(fTargets[i] || '').startsWith('item:') && (
                        <span className="ml-1.5 text-[10px] text-vblue">→ existing expense</span>)}
                      <span className="text-slate-500 text-xs block">{g.insight}</span>
                      {g.note && <span className="text-vpurple/90 text-xs block">✨ {g.note}</span>}
                    </span>
                    <span className="font-medium text-right">
                      {fmtR(g.total)}
                      <span className="block text-[10px] text-slate-500 font-normal">
                        = {fFormulas[i] ?? gLive(g, i).formula}
                        {Object.keys(fDropped[i] || {}).length > 0 &&
                          ` · ${Object.keys(fDropped[i]).length} unticked`}
                      </span>
                    </span>
                  </div>
                ))}
                <p className="text-[11px] text-slate-600 mt-1">
                  Click a line to rename it, change the group, adjust the formula, or point
                  it at an existing expense. Approving a formula line unticks its individual entries above — nothing
                  gets added twice. The observed spend lands as "used", and the item gets
                  ±unit steppers on the month.
                </p>
              </Section>
            )}

            {(preview.recurring_suggestions || []).length > 0 && (
              <Section id="imp-recurring" title="Looks like a monthly bill — make it one recurring expense?">
                {preview.recurring_suggestions.map((r, i) => (
                  <label key={i} className="flex items-center gap-3 py-2 flex-wrap">
                    <input type="checkbox" checked={!!combines[i]}
                           onChange={() => setCombines((c) => ({ ...c, [i]: !c[i] }))} />
                    <span className="flex-1 min-w-[160px]">
                      {combines[i] ? (
                        <input className="input !py-0.5 !px-2 text-sm !w-48"
                               placeholder={r.title} title="Name it what YOU call it"
                               onClick={(e) => e.preventDefault()}
                               value={cRenames[i] ?? r.title}
                               onChange={(e) => setCRenames((v) => ({ ...v, [i]: e.target.value }))} />
                      ) : r.title}
                      <span className="text-slate-500 text-xs block">
                        {r.single_month
                          ? 'Looks like a debit order or subscription'
                          : `Seen ${r.count}× across ${r.months_seen.length} months`}
                        {r.expected_day && ` · goes off around the ${ordinal(r.expected_day)}`}
                        {r.last_seen && ` · last ${r.last_seen}`}
                        {r.category && ` · ${r.category}`}
                        {r.confidence === 'high' && ' · high confidence'}
                      </span>
                    </span>
                    {combines[i] ? (
                      <span className="flex gap-1.5" onClick={(e) => e.preventDefault()}>
                        {(r.options || []).length > 1 && (
                          <select className="input !w-auto !py-0.5 !px-1.5 text-xs"
                                  title="Which spend becomes the recurring amount? Latest first."
                                  value={cAmts[i] ?? r.amount}
                                  onChange={(e) => setCAmts((v) => ({ ...v, [i]: e.target.value }))}>
                            {r.options.map((o, k) => (
                              <option key={k} value={o.amount}>{o.date} · R {o.amount}</option>
                            ))}
                          </select>
                        )}
                        <input className="input !py-0.5 !px-2 text-sm !w-32 text-right"
                               title="Monthly amount — or a formula like 40*12"
                               value={cAmts[i] ?? r.amount}
                               onChange={(e) => setCAmts((v) => ({ ...v, [i]: e.target.value }))} />
                      </span>
                    ) : (
                      <span className="font-medium">{fmtR(r.amount)}/mo</span>
                    )}
                    {combines[i] && (
                      <button type="button" className="text-xs text-vpurple underline-offset-2 hover:underline"
                              onClick={(e) => { e.preventDefault(); setRecAdv((v) => ({ ...v, [`r${i}`]: !v[`r${i}`] })); }}>
                        {recAdv[`r${i}`] ? 'Hide advanced' : 'Advanced'}
                      </button>
                    )}
                    {combines[i] && recAdv[`r${i}`] && (
                      <span className="basis-full flex flex-wrap gap-3 pl-7 text-xs text-slate-400"
                            onClick={(e) => e.preventDefault()}>
                        <span className="flex items-center gap-1.5">
                          Goes off on day
                          <input type="number" min="1" max="28" className="input !w-16 !py-0.5 !px-2 text-sm"
                                 title="Day of the month it usually goes off (1–28)"
                                 value={cDays[i] ?? (r.expected_day || '')}
                                 onChange={(e) => setCDays((v) => ({ ...v, [i]: e.target.value }))} />
                        </span>
                        <span className="flex items-center gap-1.5">
                          Category
                          <input className="input !w-40 !py-0.5 !px-2 text-sm" list="vault-cats"
                                 value={cCats[i] ?? (r.category || '')}
                                 onChange={(e) => setCCats((v) => ({ ...v, [i]: e.target.value }))} />
                        </span>
                        <span>Amount: pick a recent charge or type a formula such as 40*12.</span>
                      </span>
                    )}
                  </label>
                ))}
                <p className="text-slate-500 text-xs mt-1">
                  Ticked ones become one recurring expense that repeats every month, and this month is marked paid.
                </p>
              </Section>
            )}

            {(preview.price_changes || []).length > 0 && (
              <Section id="imp-prices" title="A recurring was charged a new amount — update it?">
                {preview.price_changes.map((p, i) => (
                  <label key={i} className="flex items-center gap-3 py-2 flex-wrap">
                    <input type="checkbox" checked={!!priceOk[i]}
                           onChange={() => setPriceOk((v) => ({ ...v, [i]: !v[i] }))} />
                    <span className="flex-1 min-w-[160px]">
                      {p.title}
                      <span className="text-slate-500 text-xs block">
                        {p.date} · {p.description}
                      </span>
                    </span>
                    <span className="font-medium">
                      <span className="text-slate-500 line-through mr-2">{fmtR(p.old_amount)}</span>
                      {fmtR(pAmts[i] ?? p.new_amount)}/mo
                    </span>
                    <button type="button" className="text-xs text-vpurple underline-offset-2 hover:underline"
                            onClick={(e) => { e.preventDefault(); setRecAdv((v) => ({ ...v, [`p${i}`]: !v[`p${i}`] })); }}>
                      {recAdv[`p${i}`] ? 'Hide advanced' : 'Advanced'}
                    </button>
                    {recAdv[`p${i}`] && (
                      <span className="basis-full flex items-center gap-1.5 pl-7 text-xs text-slate-400"
                            onClick={(e) => e.preventDefault()}>
                        Use a different amount
                        <input className="input !w-28 !py-0.5 !px-2 text-sm text-right" inputMode="decimal"
                               value={pAmts[i] ?? p.new_amount}
                               onChange={(e) => setPAmts((v) => ({ ...v, [i]: e.target.value }))} />
                      </span>
                    )}
                  </label>
                ))}
                <p className="text-slate-500 text-xs mt-1">
                  Ticked ones change the recurring from this month on. Past months stay as they were.
                </p>
              </Section>
            )}

            {(preview.not_seen || []).length > 0 && (
              <Section id="imp-notseen" title="Recurring not on this statement">
                {preview.not_seen.map((n, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 flex-wrap">
                    <span className="flex-1 min-w-[160px]">
                      {n.title}
                      <span className="text-slate-500 text-xs block">
                        {n.maybe_cancelled
                          ? 'Not charged anywhere on this statement — was it cancelled?'
                          : n.expected_day
                            ? `Usually goes off around the ${ordinal(n.expected_day)} — not debited yet`
                            : 'Not debited yet this month'}
                      </span>
                    </span>
                    <span className="font-medium">{fmtR(n.amount)}</span>
                    <span className="basis-full flex flex-wrap gap-1.5 pl-0 sm:pl-2" role="group"
                          aria-label={`What to do with ${n.title}`}>
                      {[['leave', 'Leave it'], ['paid', 'I paid it another way'],
                        ['pause', n.maybe_cancelled ? 'It was cancelled — pause it' : 'Pause it']]
                        .map(([v, label]) => {
                          const on = (nsAct[i] || 'leave') === v;
                          return (
                            <button key={v} type="button" aria-pressed={on}
                                    className={`text-xs rounded-full border px-3 py-1 ${on
                                      ? 'border-vpurple text-vpurple' : 'border-edge text-slate-400'}`}
                                    onClick={() => setNsAct((a2) => ({ ...a2, [i]: v }))}>
                              {label}
                            </button>
                          );
                        })}
                    </span>
                  </div>
                ))}
                <p className="text-slate-500 text-xs mt-1">
                  Leave it keeps everything as it is. Paid another way ticks this month off. Pause stops it appearing in new months — nothing is deleted, and you can switch it back on in Manage.
                </p>
              </Section>
            )}

            {(preview.new_items || []).length > 0 && (
              <Section id="imp-new" title="New spending to add — category suggested, pick the bank it pays from">
                {(() => {
                  const visIdx = (preview.new_items || []).map((_, i) => i)
                    .filter((i) => { const n = preview.new_items[i];
                      return !(consumedDescs.has(n.description) && !freedFps.has(n.fp)); });
                  const mkey = (n) => (n.suggested_title || n.description || '')
                    .toLowerCase().replace(/\s+/g, ' ').trim();
                  const gmap = {};
                  visIdx.forEach((i) => { const n = preview.new_items[i]; const k = mkey(n);
                    (gmap[k] = gmap[k] || { key: k, idxs: [], label: n.suggested_title || n.description }).idxs.push(i); });
                  return Object.values(gmap).map((mg) => {
                    if (mg.idxs.length < 2) return renderNewRow(mg.idxs[0]);
                    const total = mg.idxs.reduce((s2, i) => s2 + Number(preview.new_items[i].amount || 0), 0);
                    const allOn = mg.idxs.every((i) => adds[i]);
                    const someOn = mg.idxs.some((i) => adds[i]);
                    const nOn = mg.idxs.filter((i) => adds[i]).length;
                    const open = !!openMerch[mg.key];
                    return (
                      <div key={'mg-' + mg.key} className="border border-edge rounded-xl overflow-hidden">
                        <div className="flex items-center gap-2 px-2 py-2">
                          <input type="checkbox" checked={allOn}
                                 ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                                 onChange={() => setAdds((a) => { const nx = { ...a }; const t = !allOn; mg.idxs.forEach((i) => { nx[i] = t; }); return nx; })} />
                          <button type="button" className="flex-1 min-w-0 text-left flex items-center gap-2"
                                  onClick={() => setOpenMerch((o) => ({ ...o, [mg.key]: !o[mg.key] }))}>
                            <span className="text-sm font-semibold truncate">{mg.label}</span>
                            <span className="text-xs text-slate-500 shrink-0">{mg.idxs.length} lines{nOn ? ` · ${nOn} ticked` : ''}</span>
                          </button>
                          <span className="font-medium shrink-0">{fmtR(total)}</span>
                          <button type="button" className="text-slate-500 text-sm shrink-0 w-5 text-center"
                                  onClick={() => setOpenMerch((o) => ({ ...o, [mg.key]: !o[mg.key] }))}>{open ? '▾' : '▸'}</button>
                        </div>
                        {open && (
                          <div className="border-t border-edge/60 pl-3 pr-1">
                            {mg.idxs.map((i) => renderNewRow(i))}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}

                {/* Bind the ⊕-picked lines to ONE expense — existing or new,
                    plain total or formula; remembered for future imports. */}
                {Object.values(groupSel).filter(Boolean).length >= 2 && (() => {
                  const idxs = (preview.new_items || []).map((_, i) => i)
                                 .filter((i) => groupSel[i]);
                  const total = idxs.reduce((s2, i) =>
                    s2 + Number(preview.new_items[i].amount || 0), 0).toFixed(2);
                  const monthExpenses = (monthItems || [])
                    .filter((x) => x.kind === 'expense');
                  const apply = () => {
                    setGroupsOut((g) => [...g, {
                      title: gForm.name.trim() || 'Grouped spend',
                      category: gForm.cat.trim(),
                      formula: gForm.formula.replace(/[x×]/gi, '*').trim(),
                      total, mode: gForm.mode,
                      item_id: gForm.mode === 'existing' ? Number(gForm.itemId) || null : null,
                      bank: stmtBank, recurring: gForm.recurring, remember: true,
                      spend: gForm.spend, extend: gForm.grow,
                      extend_formula: gForm.growF.replace(/[x×]/gi, '*').trim(),
                      member_descs: idxs.map((i) => preview.new_items[i].description),
                      member_fps: idxs.map((i) => preview.new_items[i].fp).filter(Boolean),
                    }]);
                    setAdds((a) => { const nx = { ...a }; idxs.forEach((i) => { nx[i] = false; }); return nx; });
                    setGroupSel({});
                    setGForm({ name: '', cat: '', formula: '', mode: 'new', itemId: '',
                               recurring: false, spend: true, grow: false, growF: '' });
                  };
                  return (
                    <div className="mt-2 border border-vblue/50 rounded-xl p-3">
                      <p className="text-xs text-vblue mb-2">
                        {idxs.length} lines picked · {fmtR(total)} together
                      </p>
                      <div className="flex gap-2 flex-wrap items-center">
                        <select className="input !w-auto !py-1 text-xs" value={gForm.mode}
                                onChange={(e) => setGForm((f2) => ({ ...f2, mode: e.target.value }))}>
                          <option value="new">New grouped expense</option>
                          <option value="existing">Add to an existing expense</option>
                        </select>
                        {gForm.mode === 'existing' ? (
                          <>
                            <select className="input !w-auto !py-1 text-xs" value={gForm.itemId}
                                    onChange={(e) => setGForm((f2) => ({ ...f2, itemId: e.target.value }))}>
                              <option value="">Which expense…</option>
                              {monthExpenses.map((x) =>
                                <option key={x.id} value={x.id}>{x.title}</option>)}
                            </select>
                            <button className={`text-xs px-2 py-1 rounded border ${
                                      gForm.spend ? 'border-vblue text-vblue' : 'border-edge text-slate-500'}`}
                                    title="Count the picked money as spent on it"
                                    onClick={() => setGForm((f2) => ({ ...f2, spend: !f2.spend }))}>
                              {gForm.spend ? '✓ ' : ''}spend
                            </button>
                            <button className={`text-xs px-2 py-1 rounded border ${
                                      gForm.grow ? 'border-vpurple text-vpurple' : 'border-edge text-slate-500'}`}
                                    title="Grow its formula by the picked money — either, or BOTH"
                                    onClick={() => setGForm((f2) => ({ ...f2, grow: !f2.grow }))}>
                              {gForm.grow ? '✓ ' : ''}grow formula
                            </button>
                            {gForm.grow && (
                              <input className="input !py-1 !px-2 text-xs !w-24"
                                     placeholder={`+${total} or *2`}
                                     title="The advancement — editable (+units or amounts)"
                                     value={gForm.growF}
                                     onChange={(e) => setGForm((f2) => ({ ...f2, growF: e.target.value }))} />
                            )}
                          </>
                        ) : (
                          <>
                            <input className="input !py-1 !px-2 text-sm !w-36" placeholder="Name it…"
                                   value={gForm.name}
                                   onChange={(e) => setGForm((f2) => ({ ...f2, name: e.target.value }))} />
                            <input className="input !py-1 !px-2 text-xs !w-28" list="vault-cats"
                                   placeholder="group…" value={gForm.cat}
                                   onChange={(e) => setGForm((f2) => ({ ...f2, cat: e.target.value }))} />
                            <input className="input !py-1 !px-2 text-xs !w-28"
                                   placeholder={`amount ${total} or 40*12`}
                                   title="Leave empty for the picked total, or type a formula"
                                   value={gForm.formula}
                                   onChange={(e) => setGForm((f2) => ({ ...f2, formula: e.target.value }))} />
                            <button className={`text-xs px-2 py-1 rounded border ${
                                      gForm.recurring ? 'border-vpurple text-vpurple' : 'border-edge text-slate-500'}`}
                                    onClick={() => setGForm((f2) => ({ ...f2, recurring: !f2.recurring }))}>
                              {gForm.recurring ? 'Every month' : 'Just this month'}
                            </button>
                          </>
                        )}
                        <button className="btn-primary !py-1 !px-3 text-sm"
                                disabled={gForm.mode === 'existing' && !gForm.itemId}
                                onClick={apply}>
                          Group them
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-600 mt-1.5">
                        Remembered: future imports send these merchants to the same
                        expense automatically — always shown for your approval, and a
                        transaction is never imported twice.
                      </p>
                    </div>
                  );
                })()}
                {groupsOut.length > 0 && (
                  <p className="text-xs text-vblue mt-1">
                    {groupsOut.length} group{groupsOut.length === 1 ? '' : 's'} ready —
                    applies on Approve.
                  </p>
                )}
              </Section>
            )}

            {(preview.already_applied || []).length > 0 && (
              /* Settled business from earlier imports: struck through with the
                 expense each line went to — visible, never double-counted. */
              <Section id="imp-already" title={`Already applied last import — ${
                                preview.already_applied.length} seen before`}>
                {preview.already_applied.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5 text-sm px-1">
                    <span className="text-[9px] uppercase tracking-wide text-amber-400 border border-amber-400/50 rounded px-1.5 py-0.5 whitespace-nowrap shrink-0">seen before</span>
                    <span className="flex-1 truncate line-through text-slate-500">
                      {t.description}
                    </span>
                    {t.to && (
                      <span className="text-[10px] text-vblue border border-vblue/40
                                       rounded px-1.5 py-0.5 whitespace-nowrap">
                        → {t.to}
                      </span>
                    )}
                    <span className="text-xs text-slate-600 whitespace-nowrap">{t.date}</span>
                    <span className={`line-through ${t.direction === 'in'
                            ? 'text-emerald-400/50' : 'text-slate-600'}`}>
                      {fmtR(t.amount)}
                    </span>
                  </div>
                ))}
                <p className="text-[11px] text-slate-600 mt-1">
                  Already applied last import — shown so you can see where each went. They
                  never apply twice; everything above is new spending.
                </p>
              </Section>
            )}

            {(preview.transfers || []).length > 0 && (
              <Section title="Moves between your own banks — information only">
                <label className="flex items-center gap-2 text-xs text-slate-500 py-1">
                  <input type="checkbox" checked={showTransfers}
                         onChange={() => setShowTransfers((v) => !v)} />
                  Show the {preview.transfers.length} move{preview.transfers.length === 1 ? '' : 's'} —
                  your choice; they never apply as transactions
                </label>
                {showTransfers && preview.transfers.map((t, i) => (
                  <div key={i} className="py-2 text-sm text-slate-400">
                    {fmtR(t.amount)} {t.direction} — {t.description}
                    {t.other_bank && <span className="text-vblue"> · {t.other_bank}</span>}
                  </div>
                ))}
                <p className="text-[11px] text-slate-600 mt-1">
                  Nothing to apply here — your own money moving isn't spending.
                  The "actual now" balance below is what keeps the banks true.
                </p>
              </Section>
            )}

            {preview.suggest_actual_now && (
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" checked={useBalance}
                       onChange={() => setUseBalance((v) => !v)} />
                Set {bank.bank_name ?? bank.name}'s "actual now" to the statement balance
                ({fmtR(preview.suggest_actual_now)})
              </label>
            )}

            {/* Sticky footer (Pass 7 Import, stage 4A): always-visible Apply with a
                live effect line. Counts reuse the rail's approved tallies - no new
                numbers invented; the projected leftover is a later backend-backed add. */}
            <div className="sticky bottom-0 -mx-1 px-1 pt-3 pb-1 mt-1 bg-panel/95 backdrop-blur border-t border-edge flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm text-slate-200">
                  {approvedRows > 0
                    ? `Applying ${approvedRows} change${approvedRows === 1 ? '' : 's'} to ${bank.bank_name ?? bank.name}`
                    : 'Nothing ticked yet — pick what to apply above'}
                </div>
                <div className="text-[11px] text-slate-500">
                  {approvable.filter((sx) => sx.approved > 0).map((sx) => `${sx.approved} ${sx.label.toLowerCase()}`).join(' · ')
                    || 'tick items in any section to include them'}
                </div>
              </div>
              <button className="btn-primary shrink-0 inline-flex items-center gap-2" onClick={commit} disabled={busy}>
                {busy ? <><Spinner /> Applying…</>
                  : approvedRows > 0 ? `Apply ${approvedRows} change${approvedRows === 1 ? '' : 's'}`
                  : 'Apply approved changes'}
              </button>
            </div>
            </div>
          </div>
          );
        })()}

        {/* Per-transaction configurator — everything one line can do, with
            room to breathe: type-to-search the destination, spend vs formula. */}
        {txConfig?.kind === 'add' && preview?.new_items?.[txConfig.i] && (() => {
          const i = txConfig.i; const n = preview.new_items[i];
          const t = aTargets[i] || 'new';
          const candidates = [
            ...(monthItems || []).filter((x) => x.kind === 'expense')
              .map((x) => ({ v: `item:${x.id}`, label: x.title, tag: 'this month' })),
            ...(preview.new_items || []).map((m, j) => ({ m, j }))
              .filter(({ j }) => j !== i && adds[j] && (aTargets[j] || 'new') === 'new')
              .map(({ m, j }) => ({ v: `pend:${j}`, tag: 'new — from this import',
                label: (renames[j] || '').trim() || m.suggested_title || m.description })),
            ...(preview.formula_suggestions || []).map((g2, gi) => ({ g2, gi }))
              .filter(({ gi }) => formulas[gi] && (fOwn[gi] ?? true))
              .map(({ g2, gi }) => ({ v: `fpend:${gi}`,
                tag: 'new — formula group from this import',
                label: (fRenames[gi] || '').trim() || g2.title })),
          ].filter((c) => !txQ.trim()
                          || c.label.toLowerCase().includes(txQ.trim().toLowerCase()));
          return (
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/75 backdrop-blur-[2px]" />
              <div className="relative bg-panel border border-edge rounded-xl p-6 max-w-md w-full
                              shadow-2xl max-h-[88vh] overflow-y-auto"
                   onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h3 className="font-bold">{n.suggested_title || n.description}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {n.description} · {fmtR(n.amount)} · {n.date}
                    </p>
                  </div>
                  <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                                     hover:text-white shrink-0"
                          onClick={() => setTxConfig(null)}>✕</button>
                </div>

                <p className="text-xs text-slate-500 mt-4 mb-1 uppercase tracking-wide">
                  Where does it belong?
                </p>
                <button className={`w-full text-left border rounded-xl px-3 py-2 text-sm mb-1.5 ${
                          t === 'new' ? 'border-vblue bg-vblue/10' : 'border-edge'}`}
                        onClick={() => { setATargets((v) => ({ ...v, [i]: 'new' }));
                                         setAdds((a) => ({ ...a, [i]: true })); }}>
                  ＋ Its own new expense
                </button>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <input className="input !py-1.5 text-sm flex-1"
                         placeholder="Type to search an expense…"
                         value={txQ} onChange={(e) => setTxQ(e.target.value)} />
                  <VoiceButton onText={(t) => setTxQ(t)} title="Search expenses by voice" />
                </div>
                <div className="max-h-40 overflow-y-auto grid gap-1 mb-2">
                  {candidates.map((c) => (
                    <button key={c.v}
                            className={`text-left border rounded-lg px-3 py-1.5 text-sm ${
                              t === c.v ? 'border-vblue bg-vblue/10' : 'border-edge'}`}
                            onClick={() => { setATargets((v) => ({ ...v, [i]: c.v }));
                                             setAdds((a) => ({ ...a, [i]: true })); }}>
                      {c.v.startsWith('pend:') ? '↪ ' : '→ '}{c.label}
                      <span className="text-[10px] text-slate-600 ml-1.5">{c.tag}</span>
                    </button>
                  ))}
                  {!candidates.length && (
                    <p className="text-xs text-slate-600 px-1">No match — it can be its own.</p>
                  )}
                </div>

                {t === 'new' && (() => {
                  // The name the AI proposed (editable). ✨ re-suggests the
                  // category FROM this name via the one shared assist endpoint —
                  // same path CategoryPicker uses — so the whole app has a
                  // single AI route. Both fields stay fully editable before Apply.
                  const curName = ((renames[i] ?? (n.suggested_title || n.description)) || '').trim();
                  const curCat = (aCats[i] ?? (n.category || '')).trim();
                  return (
                  <div className="grid gap-1.5">
                    <input className="input !py-1.5 text-sm" placeholder={n.suggested_title || n.description}
                           title="Name it what YOU call it"
                           value={renames[i] ?? (n.suggested_title || n.description)}
                           onChange={(e) => setRenames((r) => ({ ...r, [i]: e.target.value }))} />
                    <div className="flex gap-1.5 items-center">
                      <input className="input !py-1.5 text-xs flex-1" list="vault-cats" placeholder={n.category || 'group…'}
                             value={aCats[i] ?? (n.category || '')}
                             onChange={(e) => setACats((v) => ({ ...v, [i]: e.target.value }))} />
                      <AIAction size="sm" label=""
                                title="✨ Suggest a category from the name"
                                disabled={!curName}
                                run={() => api('/budget/items/assist/',
                                      { method: 'POST', body: { title: curName, kind: 'expense' } })
                                      .then((d) => { if (!d.category_name) {
                                        throw { data: { detail: '✨ no confident category — type your own.' } }; }
                                        return d; })}
                                onResult={(d) => setACats((v) => ({ ...v, [i]: d.category_name }))}
                                onUndo={() => setACats((v) => ({ ...v, [i]: n.category || '' }))}
                                readback={(d) => `Category: ${d.category_name} — edit before saving.`} />
                      <button className={`text-xs px-2.5 rounded-xl border ${
                                recurs[i] ? 'border-vpurple text-vpurple' : 'border-edge text-slate-500'}`}
                              title="Recurring seeds every new month; refine the cadence in Advanced below"
                              onClick={() => setRecurs((r) => {
                                const on = !r[i];
                                // Keep the Advanced cadence honest with the coarse toggle:
                                // turning recurring ON defaults to monthly, OFF clears it.
                                setACad((c) => ({ ...c, [i]: on
                                  ? { cadence: 'monthly', ...(c[i] || {}) }
                                  : { ...(c[i] || {}), cadence: 'none' } }));
                                return { ...r, [i]: on };
                              })}>
                        {recurs[i] ? 'Every month' : 'Just this month'}
                      </button>
                    </div>
                    {/* Plain-English read-back of what AI proposed — accept it,
                        rename the field, or re-pick the category before Apply. */}
                    <p className="text-[11px] text-slate-500">
                      ✨ {n.name_readback || `Suggested “${n.suggested_title || n.description}”`}
                      {curCat ? ` · filed under ${curCat}` : ''} — rename or re-pick before Apply.
                    </p>
                    {/* 📍 Link a place — editable location per line (owner spec).
                        Type to match a saved pin, or keep a plain name as a note. */}
                    <div className="flex gap-1.5 items-center">
                      <span className="text-sm shrink-0" aria-hidden>📍</span>
                      <input className="input !py-1.5 text-sm flex-1" list="vault-places"
                             placeholder="Link a place (optional)"
                             title="Where the money goes — a saved place, or a name to keep"
                             value={aLoc[i] ?? ''}
                             onChange={(e) => setALoc((v) => ({ ...v, [i]: e.target.value }))} />
                    </div>
                    {/* Make it recurring · advanced — one-month formula OR a
                        daily/weekly/monthly cadence (weekly → which days). The
                        selected transactions feed this expense going forward. */}
                    <div className="grid gap-2 rounded-xl border border-vpurple/40 bg-vpurple/5 px-3 py-3 mt-1">
                      <div className="text-[11px] uppercase tracking-wide text-vpurple">
                        Make it recurring · advanced
                      </div>
                      <input className="input !py-1.5 text-sm font-mono"
                             placeholder="One-month formula, e.g. 200*31"
                             title="A one-month formula sets the amount for this month"
                             value={aOneF[i] ?? ''}
                             onChange={(e) => setAOneF((v) => ({ ...v, [i]: e.target.value }))} />
                      <CadenceControls hideRate
                        value={aCad[i] || { cadence: recurs[i] ? 'monthly' : 'none' }}
                        onChange={(v) => {
                          setACad((c) => ({ ...c, [i]: v }));
                          // Cadence is the source of truth for recurrence here:
                          // any real cadence flips the coarse toggle on.
                          setRecurs((r) => ({ ...r, [i]: !!(v.cadence && v.cadence !== 'none') }));
                        }} />
                      <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer">
                        <input type="checkbox" checked={aFeed[i] ?? !!recurs[i]}
                               onChange={(e) => setAFeed((v) => ({ ...v, [i]: e.target.checked }))} />
                        Feed this expense from “{(n.suggested_title || n.description)}” going forward
                        <span className="text-slate-600">— future imports route it here</span>
                      </label>
                    </div>
                  </div>
                  );
                })()}
                {(t.startsWith('item:') || t.startsWith('fpend:')) && (
                  <div className="grid gap-1.5">
                    <div className="flex gap-1.5">
                      <button className={`flex-1 text-sm py-1.5 rounded-xl border ${
                                (aSpend[i] ?? true) ? 'border-vblue bg-vblue/10' : 'border-edge text-slate-400'}`}
                              title="Count this money as spent on the expense"
                              onClick={() => setASpend((v) => ({ ...v, [i]: !(v[i] ?? true) }))}>
                        {(aSpend[i] ?? true) ? '✓ ' : ''}Count as spend
                      </button>
                      <button className={`flex-1 text-sm py-1.5 rounded-xl border ${
                                aExtend[i] ? 'border-vpurple bg-vpurple/10' : 'border-edge text-slate-400'}`}
                              title="Grow the expense's formula — either, or BOTH together"
                              onClick={() => setAExtend((v) => ({ ...v, [i]: !v[i] }))}>
                        {aExtend[i] ? '✓ ' : ''}Grow its formula
                      </button>
                    </div>
                    {aExtend[i] && (
                      <input className="input !py-1.5 text-sm"
                             placeholder={`+${n.amount} — or *6, +36*2 (one unit or more)`}
                             value={aExtendF[i] ?? ''}
                             onChange={(e) => setAExtendF((v) => ({ ...v, [i]: e.target.value }))} />
                    )}
                  </div>
                )}
                <button className="btn-primary w-full mt-4 !py-2"
                        onClick={() => { setTxConfig(null); setTxQ(''); }}>
                  Done — applies on Approve
                </button>
              </div>
            </div>
          );
        })()}

        {/* GROUP configurator — the whole suggested group, editable in one
            place: name, category, formula, recurring, and its destination
            (own line, or applied onto an existing expense as spend / growth). */}
        {txConfig?.kind === 'formula' && preview?.formula_suggestions?.[txConfig.i] && (() => {
          const i = txConfig.i; const g = preview.formula_suggestions[i];
          const t = fTargets[i] || 'own';
          const pendGroups = (preview.formula_suggestions || [])
            .map((g2, gi) => ({ g2, gi }))
            .filter(({ gi }) => gi !== i && formulas[gi] && (fOwn[gi] ?? true))
            .map(({ g2, gi }) => ({ id: `fpend:${gi}`, pending: true,
              title: (fRenames[gi] || '').trim() || g2.title }));
          const candidates = [
            ...(monthItems || []).filter((x) => x.kind === 'expense'),
            ...pendGroups,
          ].filter((x) => !txQ.trim()
                          || x.title.toLowerCase().includes(txQ.trim().toLowerCase()));
          return (
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/75 backdrop-blur-[2px]" />
              <div className="relative bg-panel border border-edge rounded-xl p-6 max-w-md w-full
                              shadow-2xl max-h-[88vh] overflow-y-auto"
                   onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h3 className="font-bold">{(fRenames[i] ?? g.title) || g.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{g.insight}</p>
                    {g.note && <p className="text-xs text-vpurple/90 mt-0.5">✨ {g.note}</p>}
                  </div>
                  <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                                     hover:text-white shrink-0"
                          onClick={() => setTxConfig(null)}>✕</button>
                </div>
                <div className="flex gap-1.5 mt-4 mb-1">
                  {[['details', '📝 Details'], ['where', '🎯 Where it lands']].map(([k, label]) => (
                    <button key={k}
                            className={`flex-1 text-xs py-1.5 rounded-xl border ${
                              ovTab === k ? 'border-vblue bg-vblue/15 text-white'
                                          : 'border-edge text-slate-500'}`}
                            onClick={() => setOvTab(k)}>
                      {label}
                    </button>
                  ))}
                </div>

                {ovTab === 'details' && <>
                <div className="grid gap-1.5 mt-2">
                  <input className="input !py-1.5 text-sm" title="Name it what YOU call it"
                         value={fRenames[i] ?? g.title}
                         onChange={(e) => setFRenames((r) => ({ ...r, [i]: e.target.value }))} />
                  <div className="flex gap-1.5">
                    <input className="input !py-1.5 text-xs flex-1" list="vault-cats"
                           title="Category — type to choose or create"
                           value={fCats[i] ?? (g.category || '')}
                           onChange={(e) => setFCats((c) => ({ ...c, [i]: e.target.value }))} />
                    <input className="input !py-1.5 text-xs !w-28"
                           title="The formula — unit×count for THIS month (e.g. 36*7)"
                           value={fFormulas[i] ?? gLive(g, i).formula}
                           onChange={(e) => setFFormulas((f) => ({ ...f, [i]: e.target.value }))} />
                  </div>
                  {/* 📍 Link a place — the group's location (owner spec). */}
                  <div className="flex gap-1.5 items-center">
                    <span className="text-sm shrink-0" aria-hidden>📍</span>
                    <input className="input !py-1.5 text-sm flex-1" list="vault-places"
                           placeholder="Link a place (optional)"
                           title="Where this group's money goes — a saved place, or a name to keep"
                           value={fLoc[i] ?? ''}
                           onChange={(e) => setFLoc((v) => ({ ...v, [i]: e.target.value }))} />
                  </div>
                </div>

                {/* EVERY decision visible, always — recurring, and how the
                    money applies (spend and/or formula, together or alone). */}
                <p className="text-xs text-slate-500 mt-4 mb-1 uppercase tracking-wide">
                  Recurring?
                </p>
                <button className={`w-full text-sm py-1.5 rounded-xl border ${
                          formulaRecurs[g.match_text]
                            ? 'border-vpurple text-vpurple bg-vpurple/10'
                            : 'border-edge text-slate-400'}`}
                        title="Seeds every new month with this formula automatically"
                        onClick={() => setFormulaRecurs((r) =>
                          ({ ...r, [g.match_text]: !r[g.match_text] }))}>
                  {formulaRecurs[g.match_text]
                    ? '✓ Every month — seeds each new month'
                    : 'Just this month'}
                </button>

                {(g.members || []).length > 0 && (
                  /* Every transaction the group swallowed — untick one and it
                     returns to New spending, individually assignable again;
                     spend, formula count and fingerprints recompute live. */
                  <div className="mt-4">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide">
                      Transactions in this group
                    </p>
                    <div className="max-h-44 overflow-y-auto grid gap-0.5 pr-1">
                      {g.members.map((mm, k) => {
                        const off = !!(fDropped[i] || {})[mm.fp];
                        return (
                          <label key={k}
                                 className="flex items-center gap-2 text-xs py-1 px-1
                                            rounded hover:bg-white/[0.03] cursor-pointer">
                            <input type="checkbox" checked={!off}
                                   onChange={() => setFDropped((v) => {
                                     const cur = { ...(v[i] || {}) };
                                     if (cur[mm.fp]) delete cur[mm.fp];
                                     else cur[mm.fp] = true;
                                     return { ...v, [i]: cur };
                                   })} />
                            <span className={`flex-1 truncate ${
                                    off ? 'line-through text-slate-600' : 'text-slate-300'}`}>
                              {mm.description}
                            </span>
                            <span className="text-slate-500 whitespace-nowrap">{mm.date}</span>
                            <span className={off ? 'line-through text-slate-600' : ''}>
                              {fmtR(mm.amount)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-slate-600 mt-1">
                      Unticked lines go back to New spending below — assign them
                      anywhere on their own.
                    </p>
                  </div>
                )}

                </>}

                {ovTab === 'where' && <>
                <p className="text-xs text-slate-500 mt-2 mb-1 uppercase tracking-wide">
                  Where does the group land? One, the other, or both.
                </p>
                <button className={`w-full text-left border rounded-xl px-3 py-2 text-sm mb-1.5 ${
                          (fOwn[i] ?? true) ? 'border-vblue bg-vblue/10' : 'border-edge text-slate-500'}`}
                        title="Check or uncheck — valid together with an existing expense below"
                        onClick={() => setFOwn((v) => ({ ...v, [i]: !(v[i] ?? true) }))}>
                  {(fOwn[i] ?? true) ? '✓ ' : ''}Its own formula expense
                </button>
                {(fOwn[i] ?? true) && (
                  <div className="border border-edge rounded-xl p-2.5 mb-1.5 grid gap-1.5">
                    <div className="flex gap-1.5 items-center">
                      <span className="text-xs text-slate-500 flex-1">
                        Spent on it so far — yours to change ({fmtR(gLive(g, i).spent)} observed)
                      </span>
                      <input className="input !py-1 !px-2 text-sm !w-24 text-right"
                             title="Editable: what counts as already spent. Clear it for none."
                             value={fSpentAmt[i] ?? ((fOwnSpend[i] ?? true) ? gLive(g, i).spent : '')}
                             onChange={(e) => setFSpentAmt((v) => ({ ...v, [i]: e.target.value }))} />
                    </div>
                    <div className="flex gap-1.5 items-center">
                      <span className="text-xs text-slate-500 flex-1">
                        Items in the formula — change the calculation
                      </span>
                      {(() => {
                        const cur = ((fFormulas[i] ?? gLive(g, i).formula) || '').replace(/[x×]/gi, '*');
                        const m = cur.match(/^([0-9.]+)\*([0-9]+)$/);
                        const bump = (d) => {
                          if (!m) return;
                          const nc = Math.max(1, Number(m[2]) + d);
                          setFFormulas((f) => ({ ...f, [i]: `${m[1]}*${nc}` }));
                        };
                        return (
                          /* Same − count + reading as the dashboard stepper. */
                          <span className="flex items-center gap-2">
                            <button className="w-7 h-7 rounded-full border border-edge text-sm
                                               leading-none hover:border-vblue hover:text-vblue
                                               disabled:opacity-30 disabled:hover:border-edge"
                                    title="One item fewer"
                                    disabled={!m} onClick={() => bump(-1)}>−</button>
                            <span className="text-sm text-slate-300 min-w-[56px] text-center">
                              {cur}
                            </span>
                            <button className="w-7 h-7 rounded-full border border-edge text-sm
                                               leading-none hover:border-vblue hover:text-vblue
                                               disabled:opacity-30 disabled:hover:border-edge"
                                    title="One item more"
                                    disabled={!m} onClick={() => bump(+1)}>+</button>
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-1.5 mb-1.5">
                  <input className="input !py-1.5 text-sm flex-1"
                         placeholder="Type to search an existing expense…"
                         value={txQ} onChange={(e) => setTxQ(e.target.value)} />
                  <VoiceButton onText={(t) => setTxQ(t)} title="Search expenses by voice" />
                </div>
                <div className="max-h-32 overflow-y-auto grid gap-1 mb-2">
                  {candidates.map((x) => {
                    const val = x.pending ? x.id : `item:${x.id}`;
                    return (
                    <button key={val}
                            className={`text-left border rounded-lg px-3 py-1.5 text-sm ${
                              t === val ? 'border-vblue bg-vblue/10' : 'border-edge'}`}
                            onClick={() => setFTargets((v) => ({ ...v,
                              [i]: v[i] === val ? '' : val }))}>
                      {x.pending ? '↪ ' : '→ '}{x.title}
                      {x.pending && <span className="text-[10px] text-slate-600 ml-1.5">new — this import</span>}
                    </button>
                  ); })}
                </div>
                {(t.startsWith('item:') || t.startsWith('fpend:')) && (
                  <div className="grid gap-1.5">
                    <div className="flex gap-1.5">
                      <button className={`flex-1 text-sm py-1.5 rounded-xl border ${
                                (fSpend[i] ?? true) ? 'border-vblue bg-vblue/10' : 'border-edge text-slate-400'}`}
                              title="Count the group's money as spent on the expense"
                              onClick={() => setFSpend((v) => ({ ...v, [i]: !(v[i] ?? true) }))}>
                        {(fSpend[i] ?? true) ? '✓ ' : ''}Count as spend
                      </button>
                      <button className={`flex-1 text-sm py-1.5 rounded-xl border ${
                                fExtend[i] ? 'border-vpurple bg-vpurple/10' : 'border-edge text-slate-400'}`}
                              title="Grow the expense's formula (unit × how many) — either, or BOTH"
                              onClick={() => setFExtend((v) => ({ ...v, [i]: !v[i] }))}>
                        {fExtend[i] ? '✓ ' : ''}Grow its formula
                      </button>
                    </div>
                    {fExtend[i] && (
                      <input className="input !py-1.5 text-sm"
                             placeholder={`+${g.spent} — or *${g.count}, +${g.unit}*${g.count}`}
                             value={fExtendF[i] ?? ''}
                             onChange={(e) => setFExtendF((v) => ({ ...v, [i]: e.target.value }))} />
                    )}
                  </div>
                )}
                </>}
                <button className="btn-primary w-full mt-4 !py-2"
                        onClick={() => {
                          // A ticked group must LAND somewhere — no silent
                          // no-ops: with nothing selected, own comes back on.
                          if (!(fOwn[i] ?? true) && !(fTargets[i] || '')) {
                            setFOwn((v) => ({ ...v, [i]: true }));
                          }
                          setTxConfig(null); setTxQ('');
                          setFormulas((f) => ({ ...f, [i]: true })); }}>
                  Done — applies on Approve
                </button>
              </div>
            </div>
          );
        })()}

        {/* INCOME configurator — name, group, WHICH hand-off amount (or a
            formula), the day it normally lands, and the salary flag. */}
        {txConfig?.kind === 'income' && preview?.income_suggestions?.[txConfig.i] && (() => {
          const i = txConfig.i; const s = preview.income_suggestions[i];
          return (
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/75 backdrop-blur-[2px]" />
              <div className="relative bg-panel border border-edge rounded-xl p-6 max-w-md w-full
                              shadow-2xl max-h-[88vh] overflow-y-auto"
                   onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h3 className="font-bold">{(iRenames[i] ?? s.title) || s.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Seen {s.count}× across {s.months_seen.length} month{s.months_seen.length === 1 ? '' : 's'}
                      {s.last_seen && ` · last ${s.last_seen}`}
                    </p>
                  </div>
                  <button className="w-8 h-8 rounded-full border border-edge text-slate-400
                                     hover:text-white shrink-0"
                          onClick={() => setTxConfig(null)}>✕</button>
                </div>
                <div className="grid gap-1.5 mt-4">
                  <input className="input !py-1.5 text-sm" title="Name it what YOU call it"
                         value={iRenames[i] ?? s.title}
                         onChange={(e) => setIRenames((r) => ({ ...r, [i]: e.target.value }))} />
                  {(s.options || []).length > 1 && (
                    <select className="input !py-1.5 text-sm"
                            title="Which hand-off is the amount? Latest first."
                            value={iAmts[i] ?? s.amount}
                            onChange={(e) => setIAmts((v) => ({ ...v, [i]: e.target.value }))}>
                      {s.options.map((o, k) => (
                        <option key={k} value={o.amount}>{o.date} · R {o.amount}</option>
                      ))}
                    </select>
                  )}
                  <div className="flex gap-1.5">
                    <input className="input !py-1.5 text-sm flex-1 text-right"
                           title="Amount — or a formula, both editable"
                           value={iAmts[i] ?? s.amount}
                           onChange={(e) => setIAmts((v) => ({ ...v, [i]: e.target.value }))} />
                    <input className="input !py-1.5 text-xs !w-28" list="vault-cats"
                           placeholder="group…" title="Category (optional)"
                           value={iCats[i] ?? ''}
                           onChange={(e) => setICats((v) => ({ ...v, [i]: e.target.value }))} />
                    <input className="input !py-1.5 text-xs !w-20" type="number" min="1" max="28"
                           title="Day of month it normally lands — changeable later on the dashboard"
                           placeholder={s.last_seen ? `day ${Number(s.last_seen.slice(8, 10))}` : 'day…'}
                           value={iDays[i] ?? ''}
                           onChange={(e) => setIDays((v) => ({ ...v, [i]: e.target.value }))} />
                  </div>
                  <div className="flex gap-1.5">
                    <button className={`flex-1 text-sm py-1.5 rounded-xl border ${
                              iSalary[i] ? 'border-vpurple text-vpurple bg-vpurple/10'
                                         : 'border-edge text-slate-400'}`}
                            title="The salary that starts your new month (salary mode)"
                            onClick={() => setISalary((v) => ({ ...v, [i]: !v[i] }))}>
                      {iSalary[i] ? 'This is the salary ✓' : 'Mark as the salary?'}
                    </button>
                    <button className={`flex-1 text-sm py-1.5 rounded-xl border ${
                              (iRecur[i] ?? s.recurring)
                                ? 'border-vpurple text-vpurple bg-vpurple/10'
                                : 'border-edge text-slate-400'}`}
                            title="Recurring = expected every month on its day, seeded into new months"
                            onClick={() => setIRecur((v) =>
                              ({ ...v, [i]: !(v[i] ?? s.recurring) }))}>
                      {(iRecur[i] ?? s.recurring) ? 'Every month ✓' : 'Just this month'}
                    </button>
                  </div>
                  {(() => {
                    /* Master pairing at IMPORT time too — a choice, never a
                       rule: this statement's bank isn't the master's, so the
                       money probably moved from there. Untick for new money. */
                    const master = (monthItems || []).find((x) => x.salary_primary);
                    if (!master || Number(master.bank) === Number(stmtBank)) return null;
                    return (
                      <label className="flex items-center gap-2 text-sm cursor-pointer mt-1">
                        <input type="checkbox" checked={iFromMaster[i] ?? true}
                               onChange={() => setIFromMaster((v) =>
                                 ({ ...v, [i]: !(v[i] ?? true) }))} />
                        <span>
                          Deduct from ★ {master.bank_name || 'master'}
                          <span className="block text-[11px] text-slate-500">
                            money moved from the master — untick if this is new money
                          </span>
                        </span>
                      </label>
                    );
                  })()}
                </div>
                <button className="btn-primary w-full mt-4 !py-2"
                        onClick={() => { setIncomes((v) => ({ ...v, [i]: true }));
                                         setTxConfig(null); }}>
                  Done — applies on Approve
                </button>
              </div>
            </div>
          );
        })()}

        {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        <button className="mt-4 text-sm text-slate-500 hover:text-slate-300" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

function Spinner() {
  return <span aria-hidden="true" className="inline-block w-4 h-4 rounded-full border-2 border-vpurple/30 border-t-vpurple animate-spin" />;
}

/**
 * Shown while the statement is being read. A real read (unlock, extract, AI
 * matching) can take a while on a long PDF, so the user sees it is working
 * and for how long — never a frozen-looking button. No fake percentage: the
 * server does not report progress, so only the elapsed time is shown.
 */
function ImportProgress({ bankName }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div role="status" aria-live="polite"
         className="flex items-start gap-3 rounded-xl border border-vpurple/50 bg-vpurple/10 p-4">
      <span className="mt-0.5"><Spinner /></span>
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-medium">Reading your {bankName ? `${bankName} ` : ''}statement…</div>
        <div className="text-xs text-slate-400 mt-1">
          Vault is pulling out every transaction, then AI matches them to your budget,
          spots monthly bills and price changes. A long PDF can take a little while — keep this window open.
        </div>
      </div>
      <span className="text-xs text-slate-500 tabular-nums shrink-0">{secs}s</span>
    </div>
  );
}

/** 1 → 1st, 2 → 2nd, 23 → 23rd — for "goes off around the 3rd". */
function ordinal(n) {
  const v = Number(n) % 100;
  const suf = v >= 11 && v <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[v % 10] || 'th');
  return `${n}${suf}`;
}

function Section({ title, children, id }) {
  return (
    <div id={id} className="border border-edge rounded-xl p-4 scroll-mt-2">
      <h3 className="font-semibold text-sm text-slate-300 mb-1">{title}</h3>
      {children}
    </div>
  );
}
